import type { Invoice } from '../../types';

export type DerivedInvoiceStatus =
  | 'draft'
  | 'needs_pdf'
  | 'ready_to_send'
  | 'sent'
  | 'paid'
  | 'canceled'
  | 'overdue'
  | 'late_draft';

export type InvoicePaymentState = 'not_due' | 'due_soon' | 'overdue' | 'partial' | 'paid' | 'not_applicable';

export type InvoiceStatusSummary = {
  workflow: DerivedInvoiceStatus;
  payment: InvoicePaymentState;
  totalCents: number;
  paidCents: number;
  balanceCents: number;
};

function toMs(v: any): number | null {
  if (!v) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : null;
  }
  // Firestore Timestamp support (client SDK)
  if (typeof v?.toDate === 'function') {
    const d = v.toDate();
    const t = d instanceof Date ? d.getTime() : NaN;
    return Number.isFinite(t) ? t : null;
  }
  return null;
}

export function deriveInvoiceStatus(invoice: Invoice | null | undefined, now: Date = new Date()): DerivedInvoiceStatus {
  if (!invoice) return 'draft';
  if ((invoice as any).canceledAt || (invoice as any).status === 'canceled') return 'canceled';
  const nowMs = now.getTime();

  const paidAtMs = toMs((invoice as any).paidAt);
  const totalCents = Math.max(0, Number((invoice as any).totalCents ?? (invoice as any).subtotalCents ?? 0) || 0);
  const paidCents = Math.max(0, Number((invoice as any).paidAmountCents ?? 0) || 0);
  if (paidAtMs && (paidCents === 0 || paidCents >= totalCents)) return 'paid';

  const paymentDueMs = toMs((invoice as any).paymentDueDate);
  const sentAtMs = toMs((invoice as any).sentAt);
  const hasPdf = Boolean((invoice as any)?.pdf?.storagePath);
  const invoiceSendMs = toMs((invoice as any).invoiceSendDate) ?? toMs((invoice as any).invoiceDate);

  if (!hasPdf) return 'needs_pdf';

  // UX gap fix:
  if (paymentDueMs && nowMs > paymentDueMs && !sentAtMs) return 'late_draft';
  if (paymentDueMs && nowMs > paymentDueMs && sentAtMs && totalCents > paidCents) return 'overdue';

  if (sentAtMs) return 'sent';

  // ready_to_send only when we have a PDF and we are at/after send date
  if (invoiceSendMs && nowMs >= invoiceSendMs) return 'ready_to_send';

  return 'draft';
}

export function summarizeInvoiceStatus(invoice: Invoice | null | undefined, now: Date = new Date()): InvoiceStatusSummary {
  const totalCents = Math.max(0, Number((invoice as any)?.totalCents ?? (invoice as any)?.subtotalCents ?? 0) || 0);
  const recordedPaidCents = Math.max(0, Number((invoice as any)?.paidAmountCents ?? 0) || 0);
  const paidAtMs = toMs((invoice as any)?.paidAt);
  const paidCents = paidAtMs && recordedPaidCents === 0 ? totalCents : Math.min(recordedPaidCents, totalCents);
  const balanceCents = Math.max(0, totalCents - paidCents);
  const workflow = deriveInvoiceStatus(invoice, now);
  const sentAtMs = toMs((invoice as any)?.sentAt);
  const dueMs = toMs((invoice as any)?.paymentDueDate);
  const nowMs = now.getTime();

  let payment: InvoicePaymentState = 'not_applicable';
  if (paidAtMs && balanceCents === 0) payment = 'paid';
  else if (totalCents > 0 && sentAtMs) {
    if (balanceCents === 0 || workflow === 'paid') payment = 'paid';
    else if (paidCents > 0) payment = 'partial';
    else if (dueMs && nowMs > dueMs) payment = 'overdue';
    else if (dueMs && dueMs - nowMs <= 7 * 24 * 60 * 60 * 1000) payment = 'due_soon';
    else payment = 'not_due';
  }

  return { workflow, payment, totalCents, paidCents, balanceCents };
}

export function daysUntil(target: any, now: Date = new Date()): number | null {
  const t = toMs(target);
  if (!t) return null;
  const deltaMs = t - now.getTime();
  return Math.ceil(deltaMs / (24 * 60 * 60 * 1000));
}
